import React, { useLayoutEffect, useRef, useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSarLabAuth } from '../contexts/SarLabAuthContext';
import {
  CalendarDays,
  Dices,
  MonitorPlay,
  Building2,
  Trophy,
  MoreHorizontal,
  UserPlus,
  LogIn,
  LogOut,
  User,
  Settings,
  ArrowUpRight,
  ArrowDownToLine,
  ArrowUpFromLine,
  Banknote,
  CreditCard,
  X,
  Gift,
  CheckCircle,
  AlertCircle,
  Wallet,
  Phone
} from 'lucide-react';
import './CardNav.css';

// Banka listesi
const bankalar = [
  'Ziraat Bankası',
  'Garanti BBVA',
  'İş Bankası',
  'Yapı Kredi',
  'Akbank',
  'QNB Finansbank',
  'Denizbank',
  'Halkbank',
  'Vakıfbank',
  'ING Bank',
  'TEB',
  'Şekerbank',
  'Enpara',
  'Papara'
];

const CardNav: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showYatirimModal, setShowYatirimModal] = useState(false);
  const [showCekimModal, setShowCekimModal] = useState(false);
  const [showBonusKodModal, setShowBonusKodModal] = useState(false);
  const [showCallMeModal, setShowCallMeModal] = useState(false);
  const [callMeStatus, setCallMeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [callMeMessage, setCallMeMessage] = useState('');
  const [bonusKod, setBonusKod] = useState('');
  const [bonusKodStatus, setBonusKodStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [bonusKodMessage, setBonusKodMessage] = useState('');
  const [yatirimForm, setYatirimForm] = useState({ banka: '', iban: '', isimSoyisim: '', miktar: '' });
  const [cekimForm, setCekimForm] = useState({ banka: '', iban: '', isimSoyisim: '', miktar: '' });
  const navRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement[]>([]);
  const location = useLocation();
  const { currentPlayer, wallet, logout } = useSarLabAuth();

  // SarLab'da admin kontrolü - şimdilik placeholder
  const isAdmin = false; // TODO: SarLab backend'den admin rolü kontrolü eklenecek

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    if (navRef.current) {
      navRef.current.style.height = '60px';
      
      // Reverse animation for cards
      cardsRef.current.forEach((card, index) => {
        if (card) {
          setTimeout(() => {
            card.style.transform = 'translateY(50px)';
            card.style.opacity = '0';
          }, index * 20);
        }
      });
    }
  }, []);

  // Beni Ara fonksiyonu - SarLab API entegrasyonu
  const handleCallMe = async () => {
    if (!currentPlayer) return;

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    setCallMeStatus('loading');
    setCallMeMessage('');

    try {
      const API_BASE = process.env.REACT_APP_SARLAB_API_URL || 'http://localhost:3000';
      const response = await fetch(`${API_BASE}/api/public/player/call-me`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setCallMeStatus('success');
        setCallMeMessage(data.message || 'Arama talebiniz alındı. En kısa sürede sizinle iletişime geçilecektir.');
      } else {
        setCallMeStatus('error');
        setCallMeMessage(data.error || 'Bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } catch (error) {
      console.error('Beni Ara hatası:', error);
      setCallMeStatus('error');
      setCallMeMessage('Bir hata oluştu. Lütfen tekrar deneyin.');
    }
  };

  // Bonus kod kullanım fonksiyonu - SarLab API entegrasyonu için placeholder
  const handleBonusKodKullan = async () => {
    if (!currentPlayer || !bonusKod.trim()) return;

    setBonusKodStatus('loading');
    setBonusKodMessage('');

    try {
      // TODO: SarLab bonus kod API entegrasyonu
      // Şimdilik placeholder mesaj
      console.log('Bonus kod kullanılıyor:', bonusKod.trim().toUpperCase());

      setBonusKodStatus('error');
      setBonusKodMessage('Bonus kod özelliği yakında aktif olacak!');

    } catch (error) {
      console.error('Bonus kodu kullanılırken hata:', error);
      setBonusKodStatus('error');
      setBonusKodMessage('Bir hata oluştu. Lütfen tekrar deneyin.');
    }
  };

  // Sayfa değişiminde menüyü kapat
  useEffect(() => {
    // Hemen menüyü kapat
    closeMenu();
    
    // Ek güvenlik için timeout ile de kapat
    const timeoutId = setTimeout(() => {
      closeMenu();
    }, 50);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname, closeMenu]);

  // Menü durumuna göre tabIndex kontrolü
  useEffect(() => {
    const contentElement = document.querySelector('.card-nav-content');
    if (contentElement) {
      const links = contentElement.querySelectorAll('a');
      links.forEach(link => {
        if (isMenuOpen) {
          link.removeAttribute('tabIndex');
        } else {
          link.setAttribute('tabIndex', '-1');
        }
      });
    }
  }, [isMenuOpen]);

  const allNavItems = [
    { path: '/etkinlikler', label: 'Etkinlikler', icon: CalendarDays },
    { path: '/slotlar', label: 'Slotlar', icon: Dices },
    { path: '/canli-casino', label: 'Canlı Casino', icon: MonitorPlay },
    { path: '/sponsors', label: 'Sponsorlar', icon: Building2 },
    { path: '/spor-bahisleri', label: 'Spor Bahisleri', icon: Trophy },
    { path: '/diger', label: 'Diğer', icon: MoreHorizontal }
  ];

  const calculateHeight = () => {
    const navEl = navRef.current;
    if (!navEl) return 700; // Even more increased default height

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (isMobile) {
      // Mobilde sabit yükseklik kullan (scroll için)
      return 80; // 60px top bar + 20px padding
    }
    return 700; // Even more increased default height
  };

  useLayoutEffect(() => {
    const navEl = navRef.current;
    if (!navEl) return;

    // Initial state
    navEl.style.height = '60px';
    navEl.style.overflow = 'hidden';
    
    cardsRef.current.forEach(card => {
      if (card) {
        card.style.transform = 'translateY(50px)';
        card.style.opacity = '0';
        card.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
      }
    });
  }, []);

  useLayoutEffect(() => {
    const handleResize = () => {
      if (isMenuOpen) {
        const newHeight = calculateHeight();
        if (navRef.current) {
          navRef.current.style.height = `${newHeight}px`;
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen]);

  // Dışarı tıklandığında kapatma
  useLayoutEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        if (isMenuOpen) {
          closeMenu();
        }
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, closeMenu]);

  const openMenu = () => {
    setIsMenuOpen(true);
    if (navRef.current) {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (isMobile) {
        navRef.current.style.height = '90vh';
      } else {
        const height = calculateHeight();
        navRef.current.style.height = `${height}px`;
      }
      
      // Staggered animation for cards
      cardsRef.current.forEach((card, index) => {
        if (card) {
          setTimeout(() => {
            card.style.transform = 'translateY(0)';
            card.style.opacity = '1';
          }, index * 80);
        }
      });
    }
  };

  const toggleMenu = () => {
    if (!isMenuOpen) {
      openMenu();
    } else {
      closeMenu();
    }
  };

  const setCardRef = (i: number) => (el: HTMLDivElement | null) => {
    if (el) cardsRef.current[i] = el;
  };

  const getCardLinks = (category: string) => {
    switch (category) {
      case 'Etkinlikler':
        return [
          { label: 'Turnuvalar', href: '/etkinlikler/turnuvalar', icon: ArrowUpRight },
          { label: 'Promosyonlar', href: '/etkinlikler/promosyonlar', icon: ArrowUpRight }
        ];
      case 'Slotlar':
        return [
          { label: 'Lobby', href: '/slotlar/lobby', icon: ArrowUpRight },
          { label: 'Popüler Oyunlar', href: '/slotlar/populer', icon: ArrowUpRight },
          { label: 'Yeni Çıkanlar', href: '/slotlar/yeni', icon: ArrowUpRight },
          { label: 'Free Spinler', href: '/slotlar/free-spin', icon: ArrowUpRight }
        ];
      case 'Canlı Casino':
        return [
          { label: 'Canlı Lobi', href: '/canli-casino/lobi', icon: ArrowUpRight },
          { label: 'Masa Oyunları', href: '/canli-casino/masa-oyunlari', icon: ArrowUpRight },
          { label: 'Rulet', href: '/canli-casino/rulet', icon: ArrowUpRight },
          { label: 'Blackjack', href: '/canli-casino/blackjack', icon: ArrowUpRight }
        ];
      case 'Sponsorlar':
        return [
          { label: 'Sponsorlar', href: '/sponsors', icon: ArrowUpRight },
          { label: 'Sponsor Ol', href: '/sponsors/apply', icon: ArrowUpRight }
        ];
      case 'Spor Bahisleri':
        return [
          { label: 'Canlı', href: '/spor-bahisleri/canli', icon: ArrowUpRight },
          { label: 'Yaklaşan', href: '/spor-bahisleri/yaklasan', icon: ArrowUpRight },
          { label: 'Futbol', href: '/spor-bahisleri/futbol', icon: ArrowUpRight },
          { label: 'Basketbol', href: '/spor-bahisleri/basketbol', icon: ArrowUpRight }
        ];
      case 'Diğer':
        return [
          { label: 'VPN', href: '/diger/vpn', icon: ArrowUpRight },
          { label: 'Blog', href: '/diger/blog', icon: ArrowUpRight },
          { label: 'Beni Ara', href: '#call-me', icon: Phone, isCallMe: true },
          { label: 'Bonus Kod', href: '#bonus-kod', icon: Gift, isButton: true }
        ];
      default:
        return [];
    }
  };

  return (
    <>
      <div className={`card-nav-overlay ${isMenuOpen ? 'open' : ''}`} onClick={() => {
        if (isMenuOpen) {
          closeMenu();
          if (navRef.current) {
            navRef.current.style.height = '60px';
            
            // Reverse animation for cards
            cardsRef.current.forEach((card, index) => {
              if (card) {
                setTimeout(() => {
                  card.style.transform = 'translateY(50px)';
                  card.style.opacity = '0';
                }, index * 40);
              }
            });
          }
        }
      }} />
      <div className="card-nav-container">
        <nav ref={navRef} className={`card-nav ${isMenuOpen ? 'open' : ''}`}>
        <div className="card-nav-top">
        <div
          className={`hamburger-menu ${isMenuOpen ? 'open' : ''}`}
          onClick={toggleMenu}
          role="button"
          aria-label={isMenuOpen ? 'Menüyü Kapat' : 'Menüyü Aç'}
          tabIndex={0}
        >
            <div className="hamburger-line" />
            <div className="hamburger-line" />
          </div>

                 <Link to="/" className="logo-container" onClick={() => closeMenu()}>
                   <span className="logo-text dimariabet-logo">
                     <span className="dimaria-text">DIMARIA</span>
                     <span className="bet-text">BET</span>
                   </span>
                 </Link>

          {currentPlayer ? (
            <div className="flex items-center gap-2">
              {/* Bakiye Göstergesi */}
              <div className="hidden sm:flex items-center gap-1.5 px-3 rounded-lg card-nav-cta-button" style={{ marginRight: 0, background: 'rgba(0, 0, 0, 0.7)', borderColor: 'black', height:'22px'}}>
                <Wallet className="w-4 h-4 text-green-400" />
                <span className="text-sm font-semibold text-cyan-400">
                  {(wallet?.balance || 0).toLocaleString('tr-TR')} ₺
                </span>
              </div>
              <Link
                to="/profile"
                className="card-nav-cta-button"
                onClick={() => {
                  closeMenu();
                }}
              >
                <User className="w-4 h-4 mr-2" />
                {currentPlayer?.username || 'Kullanıcı'}
              </Link>
            </div>
          ) : (
            <Link
              to="/login"
              className="card-nav-cta-button"
              onClick={() => {
                closeMenu();
              }}
            >
              <LogIn className="w-4 h-4 mr-2" />
              Giriş Yap
            </Link>
          )}
        </div>

        <div
          className={`card-nav-content ${!isMenuOpen ? 'inert' : ''}`}
          // @ts-ignore - inert is a valid HTML attribute but not typed in React yet
          inert={!isMenuOpen ? true : undefined}
        >
          {allNavItems.map((item, idx) => (
            <div
              key={`${item.label}-${idx}`}
              className="nav-card"
              ref={setCardRef(idx)}
            >
              <div className="nav-card-label">{item.label}</div>
              <div className="nav-card-links">
                {getCardLinks(item.label).map((link: any, i) => (
                  link.isCallMe ? (
                    <button
                      key={`${link.label}-${i}`}
                      className="nav-card-link"
                      onClick={() => {
                        closeMenu();
                        if (currentPlayer) {
                          setShowCallMeModal(true);
                        } else {
                          window.location.href = '/login';
                        }
                      }}
                    >
                      <link.icon className="nav-card-link-icon" aria-hidden="true" />
                      {link.label}
                    </button>
                  ) : link.isButton ? (
                    <button
                      key={`${link.label}-${i}`}
                      className="nav-card-link"
                      onClick={() => {
                        closeMenu();
                        if (currentPlayer) {
                          setShowBonusKodModal(true);
                        } else {
                          // Giriş yapmamış kullanıcılar için login'e yönlendir
                          window.location.href = '/login';
                        }
                      }}
                    >
                      <link.icon className="nav-card-link-icon" aria-hidden="true" />
                      {link.label}
                    </button>
                  ) : (
                    <Link
                      key={`${link.label}-${i}`}
                      className="nav-card-link"
                      to={link.href}
                      onClick={() => {
                        // Hemen menüyü kapat
                        closeMenu();

                        // Ek güvenlik için timeout ile de kapat
                        setTimeout(() => {
                          closeMenu();
                        }, 100);
                      }}
                    >
                      <link.icon className="nav-card-link-icon" aria-hidden="true" />
                      {link.label}
                    </Link>
                  )
                ))}
              </div>
        </div>
      ))}
      
                 {/* Alt çizgi ve Kimlik doğrulama butonları */}
                 <div className="auth-section-mobile">
                   <div className="auth-divider"></div>
                   {currentPlayer ? (
                     // Kullanıcı giriş yapmış
                     <div className="auth-buttons-container">
                       {isAdmin && (
                         <>
                           <Link
                             to="/admin"
                             className="auth-button-mobile admin-button"
                             onClick={() => {
                               closeMenu();
                             }}
                           >
                             <Settings className="w-5 h-5" />
                             Admin
                           </Link>
                           <div className="auth-button-divider"></div>
                         </>
                       )}
                       <div className="logout-yatirim-cekim-row">
                         <button
                           className="auth-button-mobile logout-button"
                           style={{ width: '70%' }}
                           onClick={() => {
                             logout();
                             closeMenu();
                           }}
                         >
                           <LogOut className="w-5 h-5" />
                           Çıkış Yap
                         </button>
                         <button
                           className="auth-button-mobile yatirim-button"
                           style={{ width: '15%' }}
                           onClick={() => {
                             setShowYatirimModal(true);
                             closeMenu();
                           }}
                           title="Yatırım Yap"
                         >
                           <ArrowDownToLine className="w-5 h-5" />
                         </button>
                         <button
                           className="auth-button-mobile cekim-button"
                           style={{ width: '15%' }}
                           onClick={() => {
                             setShowCekimModal(true);
                             closeMenu();
                           }}
                           title="Çekim Yap"
                         >
                           <ArrowUpFromLine className="w-5 h-5" />
                         </button>
                       </div>
                     </div>
                   ) : (
                     // Kullanıcı giriş yapmamış
                     <div className="auth-buttons-container">
                       <Link
                         to="/login"
                         className="auth-button-mobile login-button"
                         onClick={() => {
                           closeMenu();
                         }}
                       >
                         <LogIn className="w-5 h-5" />
                         Giriş Yap
                       </Link>
                       <div className="auth-button-divider"></div>
                       <Link
                         to="/register"
                         className="auth-button-mobile register-button"
                         onClick={() => {
                           closeMenu();
                         }}
                       >
                         <UserPlus className="w-5 h-5" />
                         Kayıt Ol
                       </Link>
                     </div>
                   )}
                 </div>
    </div>
        </nav>
      </div>

      {/* Yatırım Modal */}
      <AnimatePresence>
        {showYatirimModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={() => setShowYatirimModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-dark-800 rounded-2xl p-6 w-full max-w-md border border-dark-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                    <Banknote className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Yatırım Yap</h3>
                </div>
                <button
                  onClick={() => setShowYatirimModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Banka Seçin</label>
                  <select
                    value={yatirimForm.banka}
                    onChange={(e) => setYatirimForm({ ...yatirimForm, banka: e.target.value })}
                    className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary-500"
                  >
                    <option value="">Banka Seçin</option>
                    {bankalar.map((banka) => (
                      <option key={banka} value={banka}>{banka}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">IBAN Numarası</label>
                  <input
                    type="text"
                    value={yatirimForm.iban}
                    onChange={(e) => setYatirimForm({ ...yatirimForm, iban: e.target.value })}
                    placeholder="TR00 0000 0000 0000 0000 0000 00"
                    className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">İsim Soyisim</label>
                  <input
                    type="text"
                    value={yatirimForm.isimSoyisim}
                    onChange={(e) => setYatirimForm({ ...yatirimForm, isimSoyisim: e.target.value })}
                    placeholder="Ad Soyad"
                    className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Miktar (₺)</label>
                  <input
                    type="number"
                    value={yatirimForm.miktar}
                    onChange={(e) => setYatirimForm({ ...yatirimForm, miktar: e.target.value })}
                    placeholder="0.00"
                    className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
                  />
                </div>

                <button
                  onClick={() => {
                    console.log('Yatırım Emri:', yatirimForm);
                    setShowYatirimModal(false);
                    setYatirimForm({ banka: '', iban: '', isimSoyisim: '', miktar: '' });
                  }}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity"
                >
                  Emir Ver
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Çekim Modal */}
      <AnimatePresence>
        {showCekimModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={() => setShowCekimModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-dark-800 rounded-2xl p-6 w-full max-w-md border border-dark-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Çekim Yap</h3>
                </div>
                <button
                  onClick={() => setShowCekimModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Banka Seçin</label>
                  <select
                    value={cekimForm.banka}
                    onChange={(e) => setCekimForm({ ...cekimForm, banka: e.target.value })}
                    className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary-500"
                  >
                    <option value="">Banka Seçin</option>
                    {bankalar.map((banka) => (
                      <option key={banka} value={banka}>{banka}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">IBAN Numarası</label>
                  <input
                    type="text"
                    value={cekimForm.iban}
                    onChange={(e) => setCekimForm({ ...cekimForm, iban: e.target.value })}
                    placeholder="TR00 0000 0000 0000 0000 0000 00"
                    className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">İsim Soyisim</label>
                  <input
                    type="text"
                    value={cekimForm.isimSoyisim}
                    onChange={(e) => setCekimForm({ ...cekimForm, isimSoyisim: e.target.value })}
                    placeholder="Ad Soyad"
                    className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Miktar (₺)</label>
                  <input
                    type="number"
                    value={cekimForm.miktar}
                    onChange={(e) => setCekimForm({ ...cekimForm, miktar: e.target.value })}
                    placeholder="0.00"
                    className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
                  />
                </div>

                <button
                  onClick={() => {
                    console.log('Çekim Emri:', cekimForm);
                    setShowCekimModal(false);
                    setCekimForm({ banka: '', iban: '', isimSoyisim: '', miktar: '' });
                  }}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity"
                >
                  Emir Ver
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bonus Kod Modal */}
      <AnimatePresence>
        {showBonusKodModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={() => {
              setShowBonusKodModal(false);
              setBonusKod('');
              setBonusKodStatus('idle');
              setBonusKodMessage('');
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-dark-800 rounded-2xl p-6 w-full max-w-md border border-dark-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Bonus Kod Kullan</h3>
                </div>
                <button
                  onClick={() => {
                    setShowBonusKodModal(false);
                    setBonusKod('');
                    setBonusKodStatus('idle');
                    setBonusKodMessage('');
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Bonus Kodunuz</label>
                  <input
                    type="text"
                    value={bonusKod}
                    onChange={(e) => setBonusKod(e.target.value.toUpperCase())}
                    placeholder="Örn: DIMARIABET750"
                    className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 uppercase"
                    disabled={bonusKodStatus === 'loading'}
                  />
                </div>

                {/* Status Messages */}
                {bonusKodMessage && (
                  <div className={`flex items-center space-x-2 p-3 rounded-lg ${
                    bonusKodStatus === 'success'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {bonusKodStatus === 'success' ? (
                      <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    )}
                    <span className="text-sm">{bonusKodMessage}</span>
                  </div>
                )}

                {/* Current Balance */}
                {wallet && (
                  <div className="bg-dark-700/50 rounded-lg p-3 flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Mevcut Bakiye:</span>
                    <span className="text-white font-bold">{wallet.balance || 0} TL</span>
                  </div>
                )}

                <button
                  onClick={handleBonusKodKullan}
                  disabled={bonusKodStatus === 'loading' || !bonusKod.trim()}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {bonusKodStatus === 'loading' ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Kontrol Ediliyor...
                    </>
                  ) : (
                    'Kodu Kullan'
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  Bonus kodları tek kullanımlıktır ve her hesap için bir kez kullanılabilir.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Beni Ara Modal */}
      <AnimatePresence>
        {showCallMeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={() => {
              setShowCallMeModal(false);
              setCallMeStatus('idle');
              setCallMeMessage('');
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-dark-800 rounded-2xl p-6 w-full max-w-md border border-dark-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Beni Ara</h3>
                </div>
                <button
                  onClick={() => {
                    setShowCallMeModal(false);
                    setCallMeStatus('idle');
                    setCallMeMessage('');
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {callMeStatus === 'idle' && (
                  <>
                    <p className="text-gray-300 text-center">
                      Aranmak ister misiniz? Müşteri temsilcilerimiz en kısa sürede sizinle iletişime geçecektir.
                    </p>

                    <div className="bg-dark-700/50 rounded-lg p-3 flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Kullanıcı:</span>
                      <span className="text-white font-semibold">{currentPlayer?.username}</span>
                    </div>

                    <button
                      onClick={handleCallMe}
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center"
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      Beni Arayın
                    </button>
                  </>
                )}

                {callMeStatus === 'loading' && (
                  <div className="flex flex-col items-center py-4">
                    <svg className="animate-spin h-10 w-10 text-cyan-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-gray-300">Talebiniz gönderiliyor...</span>
                  </div>
                )}

                {callMeStatus === 'success' && (
                  <div className="flex flex-col items-center py-4">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <span className="text-green-400 text-center">{callMeMessage}</span>
                    <button
                      onClick={() => {
                        setShowCallMeModal(false);
                        setCallMeStatus('idle');
                        setCallMeMessage('');
                      }}
                      className="mt-4 px-6 py-2 bg-dark-700 text-white rounded-lg hover:bg-dark-600 transition-colors"
                    >
                      Tamam
                    </button>
                  </div>
                )}

                {callMeStatus === 'error' && (
                  <div className="flex flex-col items-center py-4">
                    <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
                      <AlertCircle className="w-8 h-8 text-red-400" />
                    </div>
                    <span className="text-red-400 text-center">{callMeMessage}</span>
                    <button
                      onClick={() => setCallMeStatus('idle')}
                      className="mt-4 px-6 py-2 bg-dark-700 text-white rounded-lg hover:bg-dark-600 transition-colors"
                    >
                      Tekrar Dene
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CardNav;
